import { Box, Button, IconButton, MenuItem, Select, Typography, useTheme } from '@mui/material'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react'

import DiscussionForumFilterButton from '../../components/discussion-forum/DiscussionForumFilterButton'
import CreateDiscussionDialog from '../../components/discussion-forum/CreateDiscussionDialog'
import DiscussionCard from '../../components/discussion-forum/DiscussionCard'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import { useDiscussionForum } from '../../store/discussionforum.store'
import noDiscussionsImg from '../../assets/no-discussions.svg'
import { useUserStore } from '../../store/user.store'
import { Icons } from '../../utils/utilities'

const Discussions = observer( () => {

    const [searchParams, setSearchParams] = useSearchParams( { sort: 'latest', category: "", searchText: "", page: 0, discussionsPerPage: 2 } )
    const searchText = searchParams.get( 'searchText' ) || ""
    const categories = ( searchParams.getAll( 'category' ) || [] ).filter( cat => cat )
    const page = +( searchParams.get( 'page' ) || 0 )
    const sort = searchParams.get( 'sort' ) || 'latest'

    const [createDialogStatus, setCreateDialogStatus] = useState( false )
    const [discussionsPerPage, setDiscussionsPerPage] = useState( 20 )
    // const [paginationDetails, setPaginationDetails] = useState( { from: 1, to: discussionsPerPage, pageValue: 1, maxPagePossible: 1 } )
    const [loading, setLoading] = useState( true )

    const DiscussionForumStore = useDiscussionForum()
    const UserStore = useUserStore()
    const { palette, border, layout } = useTheme()

    const { id } = useParams()

    const navigate = useNavigate()

    const setSearchParamValue = ( key, val ) => {
        setSearchParams( prev => {
            prev.set( key, val )
            return prev
        }, { replace: true } )
    }

    const getData = async () => {
        setLoading( true )
        await DiscussionForumStore.fetchDiscussions( { sort, category: categories.join( "," ), search: searchText } )
        setLoading( false )
    }

    const removeCategory = ( cat ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.delete( 'category' )
            const newCategoriesList = categories.filter( c => c !== cat )
            newCategoriesList.forEach( cat => {
                newParams.append( 'category', cat )
            } )
            return newParams
        } )
    }

    const addCategoryToFilter = ( cat ) => {
        let newCategories = ( cat.trim() || '' ).split( "," ).map( c => c?.trim() )

        newCategories = new Set( newCategories.filter( c => c ) )

        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            const newCatgoriesList = new Set( [...categories, ...newCategories] )
            newParams.delete( 'category' )
            Array.from( newCatgoriesList ).forEach( cat => {
                newParams.append( 'category', cat )
            } )
            return newParams
        } )
    }

    const clearFilters = () => {
        setSearchParams( prev => {
            const newValue = new URLSearchParams( prev )
            newValue.delete( 'searchText', '' )
            newValue.delete( 'category', '' )
            newValue.delete( 'latest', '' )
            return newValue
        }, { replace: true } )
    }

    const moveToPreviousPage = () => {
        if ( page > 1 )
            setSearchParamValue( 'page', page - 1 )
    }

    const moveToNextPage = () => {
        if ( page < paginationDetails.maxPagePossible )
            setSearchParamValue( 'page', page + 1 )
    }

    const result = DiscussionForumStore.getDiscussions.reduce(
        ( acc, discussion ) => {
            // Collect all discussions for later sorting (for popular)
            acc.all.push( discussion )

            // Collect latest discussions (for trending)
            acc.latest.push( discussion )
            // Sort latest by date descending and keep only latest 10
            acc.latest.sort( ( a, b ) => new Date( b.created_date ) - new Date( a.created_date ) )
            acc.latest = acc.latest.slice( 0, 3 )

            return acc
        },
        { all: [], latest: [] }
    )

    // Derive popular (top 5 by views)
    const popular = result.all
        .sort( ( a, b ) => b.views - a.views )
        .slice( 0, 5 ).flatMap( ( d ) => d.categories || [] )

    // Derive trending categories (from latest 10)
    const trending = [
        ...new Set( result.latest
            .sort( ( a, b ) => b.views - a.views )
            .slice( 0, 5 ).flatMap( ( d ) => d.categories || [] ) )
    ]


    // useEffect( () => {
    const paginationDetails = useMemo( () => {

        let pageValue = page, from = 1, to = discussionsPerPage, data = DiscussionForumStore.getDiscussions
        const maxPagePossible = Math.ceil( data.length / discussionsPerPage )
        if ( page <= 1 ) {
            pageValue = 1
            from = 1
            to = discussionsPerPage
        }
        else if ( page >= maxPagePossible ) {
            pageValue = maxPagePossible
            to = data.length
            from = ( pageValue - 1 ) * discussionsPerPage + 1
        } else {
            from = ( page - 1 ) * discussionsPerPage + 1
            to = page * discussionsPerPage
        }
        if ( to > data.length )
            to = data.length
        return ( { pageValue, from, to, maxPagePossible } )
    }, [page, discussionsPerPage, DiscussionForumStore.getDiscussions] )
    // }, [page, discussionsPerPage, DiscussionForumStore, calculatePaginationDetails] )

    const stringifiedCategories = useMemo( () => {
        return categories.join( "," )
    }, [categories] )

    useEffect( () => {
        const getData = async () => {
            await DiscussionForumStore.fetchDiscussions( { sort, category: stringifiedCategories, search: searchText } )
            setLoading( false )
        }
        getData()
    }, [DiscussionForumStore, sort, stringifiedCategories, searchText] )


    return (
        <Box padding={layout.pagePadding} overflow="auto" display="flex" flexDirection="column" flexGrow={1}>

            <CreateDiscussionDialog getData={getData} state={createDialogStatus} setState={setCreateDialogStatus} />

            <Box flexGrow={1} sx={{ display: "flex", flexDirection: "column", overflow: "auto", gap: layout.sectionsGap }}>
                <Box
                    display="flex"
                    position="relative"
                    alignItems="center"
                    flexWrap="wrap"
                    gap="10px"
                    justifyContent="space-between">
                    <Box position="relative" sx={{ zIndex: 10 }} >
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">Discussions</Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary'>Browse discussions from wide range of categories here...</Typography>

                    </Box>
                    <Box display="flex" gap="10px" alignItems="center" flexWrap="wrap" >
                        <Button onClick={() => setCreateDialogStatus( true )} variant="contained" disableElevation startIcon={Icons.default.AddIcon} sx={{ textTransform: "capitalize", flexGrow: { xs: "1", sm: "initial" } }}>Create</Button>
                        <Button onClick={() => navigate( UserStore.getUser.user_role !== "STUDENT" ? '/faculty/my-discussions' : '/student/my-discussions' )} color='secondary' variant="outlined" startIcon={Icons.default.Discussions} sx={{ textTransform: "capitalize", flexGrow: { xs: "1", sm: "initial" } }}>My Discussions</Button>
                    </Box>
                </Box>

                {!loading && <Box flexGrow={1} display="flex" overflow="auto" flexDirection="column">

                    {/* Filter and details bar */}
                    <Box padding="10px 0" display="flex" flexDirection="column" gap="10px">

                        <Box display="flex" gap="10px" flexWrap="wrap" justifyContent="space-between" alignItems="center">
                            <DiscussionForumFilterButton />
                            {DiscussionForumStore.getDiscussions.length > 0 && <Typography variant="subtitle2"> {DiscussionForumStore.getDiscussions.length} discussions found</Typography>}
                        </Box>
                        {( categories.length > 0 || searchText ) && <Box display="flex" gap="10px" color={palette.background.default} flexWrap="wrap" alignItems="center">
                            {searchText && <Box color={palette.common.font} bgcolor={palette.background.default} border={`2px solid ${palette.borderColor}`} textTransform="capitalize" borderRadius="20px" fontSize="14px" alignItems="center" display="flex" gap="10px" padding="2px 10px">
                                <Box display="flex" gap="10px" alignItems="center">
                                    <Box marginTop="2px" component="span" display="block" >{Icons.small.SearchIcon}</Box>
                                    <Typography display="block" variant="subtitle2" fontSize="12px" component="em" >
                                        {searchText}
                                    </Typography>
                                </Box>
                                <IconButton onClick={() => setSearchParamValue( 'searchText', "" )} size="small">
                                    {Icons.default.CloseIcon}
                                </IconButton>
                            </Box>}
                            {categories.length > 0 && categories.map( cat => (
                                <Box bgcolor={palette.background.default} border={`2px solid ${palette.borderColor}`} textTransform="capitalize" borderRadius="20px" key={cat} fontSize="14px" alignItems="center" display="flex" gap="10px" padding="2px 10px">
                                    <Typography color='textPrimary' variant="subtitle2" fontSize="12px" component="em" >{cat}</Typography>
                                    <IconButton onClick={() => removeCategory( cat )} size="small">
                                        {Icons.default.CloseIcon}
                                    </IconButton>
                                </Box>
                            ) )}
                        </Box>}
                    </Box>

                    {/* Discussions */}
                    {DiscussionForumStore.getDiscussions.length > 0 && <Box minHeight="300px" flexGrow={1} alignItems="flex-start" display="grid" overflow="auto" gridTemplateColumns={{ md: "auto 250px", xs: "1fr" }} gap="10px">
                        <Box display="flex" className="slim-custom-scrollbar" flexDirection="column" height="100%" overflow="auto" gap="10px" >
                            {DiscussionForumStore.getDiscussions
                                .slice( paginationDetails.from - 1, paginationDetails.to )
                                .map( discussion => (
                                    < DiscussionCard key={discussion._id} discussion={discussion} />
                                ) )}

                        </Box>
                        <Box height="100%" display={{ md: "flex", xs: "none" }} flexDirection="column" gap="20px" overflow="auto">
                            <Box borderRadius="10px" overflow="clip" border={`1px solid ${palette.primary.light}`} >
                                <Typography padding="10px" bgcolor={palette.primary.main} color={palette.primary.contrastText} display="block" variant='subtitle2' > {Icons.default.TrendingUp} Trending Categories</Typography>
                                {trending.length > 0 && <Box display="flex" gap="5px" flexWrap="wrap" padding="5px" >
                                    {Array.from( new Set( trending ) ).map( cat => (
                                        <Typography onClick={() => addCategoryToFilter( cat )} sx={{ cursor: "pointer" }} flexGrow={1} key={cat} variant='subtitle2' padding="2px 10px" borderRadius="5px" bgcolor={palette.contentBg} border={categories?.includes( cat ) ? `1px solid ${palette.primary.dark}` : border[1]} >{cat}</Typography>
                                    ) )}
                                </Box>}
                            </Box>
                            <Box borderRadius="10px" overflow="clip" border={`1px solid ${palette.primary.light}`} >
                                <Typography padding="10px" bgcolor={palette.primary.main} color={palette.primary.contrastText} display="block" variant='subtitle2' >Popular Categories</Typography>
                                {popular.length > 0 && <Box display="flex" gap="5px" flexWrap="wrap" padding="5px" >
                                    {Array.from( new Set( popular ) ).map( cat => (
                                        <Typography onClick={() => addCategoryToFilter( cat )} sx={{ cursor: "pointer" }} flexGrow={1} key={cat} variant='subtitle2' padding="2px 10px" borderRadius="5px" bgcolor={palette.contentBg} border={categories?.includes( cat ) ? `1px solid ${palette.primary.dark}` : border[1]} >{cat}</Typography>
                                    ) )}
                                </Box>}
                            </Box>
                        </Box>
                    </Box>}

                    {/* Pagination bar */}
                    {DiscussionForumStore.getDiscussions.length > 0 && <Box display="flex" bgcolor={palette.contentBg} alignItems="center" justifyContent="flex-end" padding="10px 20px">
                        <Typography color="textSecondary" marginRight="10px" variant='subtitle2'>Discussion per page</Typography>
                        <Select onChange={e => setDiscussionsPerPage( e.target.value )} size="small" value={discussionsPerPage}>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                        <IconButton onClick={moveToPreviousPage} disabled={paginationDetails.pageValue === 1} size="small">{Icons.default.ChevronLeftIcon}</IconButton>
                        <Typography color="textSecondary" variant="subtitle2">{`${paginationDetails.from}-${paginationDetails.to}`} of {DiscussionForumStore.getDiscussions.length}</Typography>
                        <IconButton onClick={moveToNextPage} disabled={paginationDetails.pageValue >= paginationDetails.maxPagePossible} size="small">{Icons.default.ChevronRightIcon}</IconButton>
                    </Box>}

                    {/* No discussions secction */}
                    {DiscussionForumStore.getDiscussions.length === 0 && <Box padding="20px" bgcolor={palette.contentBg} borderRadius={layout.borderRadius} display="flex" alignItems="center" justifyContent="center" flexDirection="column" gap="10px">
                        <img width="150px" src={noDiscussionsImg} alt="No discussions" />
                        <Typography variant='subtitle2' color="textSecondary">No discussions</Typography>
                        {( categories.length > 0 || searchText?.length > 0 ) &&
                            <Button onClick={clearFilters} sx={{ width: "fit-content", textTransform: "capitalize", flexGrow: 1 }} variant="outlined" color="error" startIcon={Icons.default.FilterAltOff}>Clear filters</Button>
                        }
                    </Box>}
                </Box>}

                {loading && <DataLoadingSpinner padding="0" waitingMessage="Loading discussions..." />}
            </Box>
        </Box>
    )
} )

export default Discussions


